import dayjs from 'dayjs';
import * as Yup from 'yup';

import {
  APPEARANCE_BACKGROUND_TABS,
  BACKGROUND_IMAGE_FIT_MAP,
  DATE_FORMAT,
  FIELD_TYPES,
  FONT_WEIGHTS,
  GRADIENT_DIRECTION_MAP,
  GRADIENT_DIRECTIONS,
  OPTIONAL_LABEL,
  PLATFORM_OPTIONAL_LABELS,
} from './constants';

// Widget Pro has no language selection, so anything the platform seeded resolves to Finnish.
export const getEmbedWebsiteUrl = () => {
  if (typeof window === 'undefined') return '';
  const locationParam = new URLSearchParams(window.location.search).get('location');
  if (locationParam) return locationParam;
  return typeof document !== 'undefined' && document.referrer ? document.referrer : window.location.href;
};

export const getOptionalLabel = storedLabel => {
  const trimmed = storedLabel?.trim();

  return trimmed && !PLATFORM_OPTIONAL_LABELS.includes(trimmed.toLowerCase()) ? trimmed : OPTIONAL_LABEL;
};

export const toStrCamelCase = str => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export const toCamelCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let camelKey = key
        .replace(/\s(.)/g, match => match.toUpperCase())
        .replace(/\s/g, '')
        .replace(/^(.)/, match => match.toLowerCase());
      return [camelKey, value];
    })
  );
};

export const toSnakeCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let snakeKey = key.toLowerCase().replace(/\s+/g, '_');
      return [snakeKey, value];
    })
  );
};

export const capitalizeText = text => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const getFileNameFromUrl = url => {
  if (typeof url !== 'string') return '';

  const fileName = url.split('/').pop()?.split('?')[0] || '';
  const dotIndex = fileName.lastIndexOf('.');
  const underscoreIndex = fileName.lastIndexOf('_');

  if (dotIndex === -1 || underscoreIndex === -1) return fileName;

  const name = fileName.slice(0, underscoreIndex);
  const extension = fileName.slice(dotIndex);

  return `${name}${extension}`;
};

export const replaceWhiteSpaceWithUnderscore = str => {
  return str.replace(/\s+/g, '_');
};

/**
 * Language order used to resolve a per-language label dict to a single stable string.
 * Matches the backend `settings.LANGUAGES` order so the submitted response key never
 * depends on which language the field was authored in.
 */
export const PRIMARY_LABEL_LANGUAGE_ORDER = ['fi', 'en', 'de', 'sv'];

/**
 * Resolves a lead-field label to a single plain string.
 * Mirrors the backend `primary_label_text`: returns the first non-empty value in
 * PRIMARY_LABEL_LANGUAGE_ORDER. Accepts a legacy plain string for backward safety.
 *
 * @param {Object|string} label - Per-language label dict or a legacy plain string.
 * @returns {string} The primary-language label text, or an empty string.
 */
export const primaryLabelText = label => {
  if (label && typeof label === 'object') {
    const code = PRIMARY_LABEL_LANGUAGE_ORDER.find(langCode => label[langCode]);
    return code ? label[code] : '';
  }
  return typeof label === 'string' ? label : '';
};

export const resolveRedirectUrl = (fields, values) => {
  const redirectField = fields?.find(field => field.useAsRedirect);
  if (!redirectField || redirectField.isMultiSelect) return null;

  const selectedOption = (redirectField.fieldItems || []).find(
    item => primaryLabelText(item.label) === values?.[primaryLabelText(redirectField.label)]
  );
  return selectedOption?.redirectUrl || null;
};

export const redirectParentTo = url => {
  try {
    window.top.location.href = url;
  } catch {
    window.open(url, '_top');
  }
};

/**
 * Returns the initial values for the fields based on the provided fields.
 *
 * @param {Array} fields - The fields array.
 * @returns {Object} The initial values object.
 */

export const getInitialValues = fields => {
  const values = {};
  fields?.forEach(({ label: rawLabel, type, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      values[label] = [];
    } else if (fieldType === FIELD_TYPES.DROPDOWN) {
      values[label] = '';
    } else if (fieldType === FIELD_TYPES.UPLOAD) {
      values[label] = '';
    } else if (fieldType === FIELD_TYPES.CONSENT) {
      values[label] = false;
    } else {
      values[label] = '';
    }
  });
  return values;
};

export const getFieldPlaceholder = (fieldType, label, isProcessingFiles) => {
  const isFile = fieldType === FIELD_TYPES.UPLOAD;

  if (isFile && isProcessingFiles) return 'Uploading...';

  return capitalizeText(label);
};

export const generateValidationSchema = fields => {
  const schema = {};
  const requiredMsg = 'This field is required';
  const invalidEmailMsg = 'Invalid email format';
  const numericOnlyMsg = 'Only numbers allowed';

  fields?.forEach(({ label: rawLabel, type, isRequired, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    let validator;

    if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      validator = Yup.array()
        .of(Yup.string())
        .test('nonEmptyArray', requiredMsg, value => !isRequired || (Array.isArray(value) && value.length > 0));
    } else if (fieldType === FIELD_TYPES.DROPDOWN) {
      validator = Yup.string().nullable();
      if (isRequired) {
        validator = validator.test(
          'required',
          requiredMsg,
          value => value !== null && value !== undefined && value !== ''
        );
      }
    } else {
      switch (fieldType) {
      case FIELD_TYPES.EMAIL:
        validator = Yup.string().email(invalidEmailMsg);
        break;
      case FIELD_TYPES.NUMBER:
        validator = Yup.string().matches(/^\d+$/, numericOnlyMsg);
        break;
      case FIELD_TYPES.UPLOAD:
        validator = Yup.mixed().test(
          'fileRequired',
          requiredMsg,
          value => !isRequired || (value && value.length > 0)
        );
        break;
      case FIELD_TYPES.DATE:
        validator = Yup.mixed().test('validDate', requiredMsg, value => {
          const isDate = val => val instanceof Date && !isNaN(val.getTime());

          if (!value) return !isRequired;

          return isDate(value);
        });
        break;
      case FIELD_TYPES.CONSENT:
        validator = Yup.boolean();
        break;
      case FIELD_TYPES.TEXTAREA:
      default:
        validator = Yup.string();
      }

      if (isRequired && fieldType !== FIELD_TYPES.UPLOAD && fieldType !== FIELD_TYPES.CONSENT) {
        validator = validator.required(requiredMsg);
      }
    }

    schema[label] = validator;
  });
  return Yup.object().shape(schema);
};

export const truncateText = (text, maxLength = 30) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Returns the background style object based on the provided formik values.
 *
 * @param {Object} values - The formik values object.
 * @returns {Object} The background style object.
 */

export const getBackgroundStyle = values => {
  if (!values) return {};

  const {
    fontFamily,
    heading,
    backgroundType,
    backgroundColor,
    gradientDirection,
    gradientPrimaryColor,
    gradientSecondaryColor,
    backgroundImage,
    backgroundImageFit,
  } = values;

  const styles = {
    fontFamily,
    color: heading?.textColor,
  };

  const direction =
    gradientDirection === GRADIENT_DIRECTIONS.HORIZONTAL
      ? GRADIENT_DIRECTION_MAP.HORIZONTAL
      : GRADIENT_DIRECTION_MAP.VERTICAL;

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.SOLID && backgroundColor) {
    styles.backgroundColor = backgroundColor;
  }

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.GRADIENT && gradientPrimaryColor && gradientSecondaryColor) {
    styles.backgroundImage = `linear-gradient(${direction}, ${gradientPrimaryColor}, ${gradientSecondaryColor})`;
  }

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.IMAGE) {
    if (backgroundImage?.image) {
      styles.backgroundImage = `url(${backgroundImage.image})`;
      styles.backgroundPosition = 'center';
      styles.backgroundRepeat = 'no-repeat';
      const fit = BACKGROUND_IMAGE_FIT_MAP[backgroundImageFit] || BACKGROUND_IMAGE_FIT_MAP.COVER;
      styles.backgroundSize = fit;

      // A contained picture leaves the edges bare, so the chosen colour fills them.
      if (fit === BACKGROUND_IMAGE_FIT_MAP.CONTAIN) styles.backgroundColor = backgroundColor;
    } else {
      styles.backgroundColor = backgroundColor;
    }
  }

  return styles;
};

/**
 * Returns the text style object based on the provided size, styles, alignment, and color.
 *
 * @param {number} size - The font size.
 * @param {Object} styles - The styles object.
 * @param {string} alignment - The text alignment.
 * @param {string} color - The text color.
 * @returns {Object} The text style object.
 */

export const getTextStyle = (size, styles = {}, alignment = 'center', color) => {
  const normalizedStyles = Object.values(styles).some(v => v !== undefined) ? styles : mapTextStyles(styles);

  return {
    fontSize: size ? `${size}px` : '',
    fontWeight: normalizedStyles?.bold ? FONT_WEIGHTS.BOLD : FONT_WEIGHTS.REGULAR,
    fontStyle: normalizedStyles?.italic ? 'italic' : undefined,
    textDecoration: normalizedStyles?.underline ? 'underline' : undefined,
    textAlign: alignment,
    color,
  };
};

/**
 * Maps the text styles based on the provided settings.
 *
 * @param {Object} settings - The settings object.
 * @returns {Object} The mapped text styles object.
 */

export const mapTextStyles = settings => ({
  bold: settings?.isBold ?? false,
  italic: settings?.isItalic ?? false,
  underline: settings?.isUnderlined ?? false,
});

/**
 * Truncates the file name based on the provided max length.
 *
 * @param {string} fileName - The file name to truncate.
 * @param {number} maxLength - The max length of the file name.
 * @returns {string} The truncated file name.
 */

export const truncateFileName = (fileName, maxLength = 20) => {
  const extIndex = fileName.lastIndexOf('.');
  if (extIndex === -1) return fileName;

  const namePart = fileName.substring(0, extIndex);
  const extPart = fileName.substring(extIndex);

  return namePart.length > maxLength ? namePart.substring(0, maxLength) + extPart : fileName;
};

/**
 * Sorts the fields by the sorting order.
 *
 * @param {Array} fields - The fields array.
 * @returns {Array} The sorted fields array.
 */

export const sortFieldsByOrder = fields =>
  [...(fields || [])].sort((a, b) => (a.sortingOrder ?? 0) - (b.sortingOrder ?? 0));

/**
 * Formats the date value based on the provided value.
 *
 * @param {string} value - The value to format.
 * @returns {string} The formatted date value.
 */

const formatDateValue = value => {
  const isValidDate = val => val instanceof Date && !isNaN(val.getTime());

  if (isValidDate(value)) {
    return dayjs(value).format(DATE_FORMAT.STANDARD_DATE);
  }
  return value;
};

/**
 * Formats the values for the payload based on the provided values.
 *
 * @param {Object} values - The values object.
 * @returns {Object} The formatted values object.
 */

export const formatValuesForPayload = values =>
  Object.entries(values || {}).reduce((acc, [key, value]) => {
    acc[key] = formatDateValue(value);
    return acc;
  }, {});

/**
 * Converts a hex color to a lottie color array.
 *
 * @param {string} hex - The hex color to convert.
 * @returns {Array} The lottie color array.
 */

export const hexToLottieColor = hex => {
  const r = parseInt(hex?.slice(1, 3), 16) / 255;
  const g = parseInt(hex?.slice(3, 5), 16) / 255;
  const b = parseInt(hex?.slice(5, 7), 16) / 255;
  return [r, g, b, 1];
};

/**
 * Recolors a lottie animation based on the provided fill and stroke colors.
 *
 * @param {Object} animationData - The lottie animation data.
 * @param {string} fillHex - The fill hex color.
 * @param {string} strokeHex - The stroke hex color.
 * @returns {Object} The recolored lottie animation data.
 */

export const recolorLottie = (animationData, fillHex, strokeHex) => {
  const fillColor = hexToLottieColor(fillHex);
  const strokeColor = hexToLottieColor(strokeHex);
  const cloned = structuredClone(animationData);

  const traverse = obj => {
    if (!obj || typeof obj !== 'object') return;

    // Fill layers
    if (obj.ty === 'fl' && obj.c?.k?.length === 4) {
      obj.c.k = fillColor;
    }

    // Stroke layers
    if (obj.ty === 'st' && obj.c?.k?.length === 4) {
      obj.c.k = strokeColor;
    }

    Object.values(obj).forEach(traverse);
  };

  traverse(cloned);
  return cloned;
};

/**
 * Resolves the CTA style based on the provided settings.
 *
 * @param {Object} settings - The settings object.
 * @returns {Object} The resolved CTA style object.
 */

export const resolveCtaStyle = ({ isGlobal, globalSettings, localSettings, isMobileMode, fontFamily }) => {
  const settings = isGlobal ? globalSettings : localSettings;

  const textSize = isMobileMode ? settings?.mobileTextSize : settings?.webTextSize;
  const textStyles = mapTextStyles(settings);

  return {
    ...getTextStyle(textSize, textStyles, settings?.alignment, settings?.textColor),
    fontFamily,
  };
};

/**
 * Formats the current time in the format of day-month-year:hour:minute:period.
 *
 * @returns {string} The formatted current time.
 */

export const formatTimeStamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
